/**
 * Phase 6b: a technology tree, and what climbing it unlocks.
 *
 * The first build that is a game rather than a world. There are units, they have
 * movement, the map is dark until somebody walks into it, and turns pass.
 *
 * The shell owns exactly two things the engine does not: what is SELECTED, and where
 * saves are kept. Selection is not game state — two people watching the same game may
 * reasonably disagree about it, and a save that restored somebody else's cursor would
 * be wrong — so it lives here and nowhere near GameState.
 *
 * The panel of generation sliders is a development tool and will not ship; the game
 * gets curated named presets at new-game setup instead.
 */

import { createEngine, loadEngine, ERROR_TEXT, type Engine } from '../core/engine.js';
import { carrierAt, planStrike } from '../core/combat/air.js';
import { barrage } from '../core/combat/index.js';
import { resourceAccess, resourcesAt } from '../core/resources/access.js';
import { resourceKnown } from '../core/resources/known.js';
import { createNewGameScreen } from './new-game-screen.js';
import { createMinimap, type MinimapView } from './minimap.js';
import { tileCount, tileIndex } from '../core/state/grid.js';
import { validateWorld } from '../core/map/validate.js';
import { findRegions, openWaterStats } from '../core/map/hydrology.js';
import type { MapgenOverrides } from '../core/map/params.js';
import type { WorldReport } from '../core/map/generate.js';
import { reachableTiles } from '../core/path/astar.js';
import { cargoOf, maxHp, rankOf, unitDef, unitIndex, unitsOf } from '../core/units/index.js';
import { exploredFraction } from '../core/vision/index.js';
import { cityAt, citiesOf, cityYield, foodSurplus, growthThreshold, tileYield } from '../core/cities/index.js';
import { culturePerTurn } from '../core/culture/index.js';
import { capText, isAtSizeCap, itemCost, itemName, sizeCap } from '../core/cities/production.js';
import { budget, dragFraction, effectiveYield, mood } from '../core/cities/economy.js';
import { cultureLine } from './culture-text.js';
import { buildableHere, canBuildImprovements, turnsRemaining } from '../core/units/work.js';
import { pathOptions } from '../core/units/move.js';
import { upgradeCost, upgradeTarget } from '../core/units/upgrade.js';
import { createCityScreen } from './city-screen.js';
import { createCitiesScreen } from './cities-screen.js';
import { createTechScreen } from './tech-screen.js';
import { createRivalsScreen } from './rivals-screen.js';
import { createGovernmentScreen } from './government-screen.js';
import { createDealScreen } from './deal-screen.js';
import { createScoreScreen } from './score-screen.js';
import { treatyLeft } from '../core/diplomacy/index.js';
import { anarchyLeft, governmentOf, inAnarchy, maxScienceRate } from '../core/government/index.js';
import { confirmDialog } from './dialog.js';
import { atWar, enemiesOf, militaryStrength } from '../core/diplomacy/index.js';
import type { MovePreview } from '../core/combat/preview.js';
import type { GameEvent } from '../core/commands/index.js';
import { currentEra, turnsToDiscover } from '../core/research/index.js';
import { NONE, VIS_UNKNOWN, VIS_VISIBLE, type City, type Unit } from '../core/state/types.js';
import { clampCamera, createCamera, centreOn, panBy, tileAtScreen, zoomTo, type Camera } from '../render/camera.js';
import { neighborsByDirection } from '../core/state/grid.js';
import { renderMap } from '../render/renderer.js';
import { buildScene, type Scene } from '../render/scene.js';
import { dimetricTileAt, renderDimetric, SQUASH } from '../render/dimetric.js';
import { modelsWaiting } from '../render/models/index.js';
import { clearSlot, listSlots, readSlot, SLOT_COUNT, writeSlot, writeAutosave, listAutosaves, readAutosave } from './saves.js';

let engine: Engine;
let report: WorldReport;
let camera: Camera;

/** Generation parameters exposed as sliders. Development only — see the file header. */
const TUNABLE = [
  { key: 'seaLevel', label: 'Sea level', min: 0.3, max: 0.85, step: 0.01 },
  { key: 'continentScale', label: 'Continent size', min: 1.4, max: 8, step: 0.1 },
  { key: 'tilesPerPlate', label: 'Plate size', min: 60, max: 600, step: 10 },
  { key: 'mountainFraction', label: 'Mountains', min: 0, max: 0.25, step: 0.005 },
  { key: 'hillFraction', label: 'Hills', min: 0, max: 0.4, step: 0.01 },
  { key: 'forestDensity', label: 'Forest', min: 0, max: 0.8, step: 0.02 },
  { key: 'rainfallRate', label: 'Rainfall', min: 0.01, max: 0.2, step: 0.005 },
  { key: 'riverDensity', label: 'Rivers', min: 0, max: 0.06, step: 0.002 },
  { key: 'resourceDensity', label: 'Resources', min: 0, max: 0.25, step: 0.005 },
] as const;

const app = document.getElementById('app')!;

// Rail icons, drawn to the house style's 22px line grid.
const ICON = {
  map: '<path d="M11 2.5l7.5 4.3v8.4L11 19.5l-7.5-4.3V6.8z"/><path d="M11 11l7.5-4.2M11 11v8.5M11 11L3.5 6.8"/>',
  cities: '<path d="M3 19V9l5-3v13M8 19V5l6-3v17M14 19V8l5 2.5V19M2 19h18"/>',
  research: '<path d="M8.5 3h5M9.5 3v5.5L4.5 17a1.5 1.5 0 0 0 1.3 2.2h10.4a1.5 1.5 0 0 0 1.3-2.2l-5-8.5V3"/><path d="M7 13.5h8"/>',
  rivals: '<circle cx="8" cy="8" r="3.2"/><path d="M2.5 18.5c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5"/><circle cx="15.5" cy="7" r="2.6"/><path d="M15 12.5c2.5 0 4.2 1.5 4.6 4.3"/>',
  government: '<path d="M3 8.5L11 3l8 5.5M4.5 8.5v8M8.5 8.5v8M13.5 8.5v8M17.5 8.5v8M2.5 19h17"/>',
  score: '<path d="M3 18.5h16M5.5 15V11M10 15V6M14.5 15V9M19 15V4"/>',
  saves: '<path d="M4 3.5h11l3 3V18.5H4z"/><path d="M7 3.5v5h7v-5M7 18.5v-6h8v6"/>',
};
const icon = (name: keyof typeof ICON): string =>
  `<svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON[name]}</svg>`;

// The main screen in the house style (ADR-0074): a white top bar, a rail of screens on the
// left, the map full-bleed beneath floating panels — selection on the right, news on the
// left — and the game's settings and saves in a panel of their own.
app.innerHTML = `
  <header class="topbar">
    <div class="brand">Epoch</div>
    <div class="sep"></div>
    <div class="civ-line"><span class="h-chip" id="civ-chip"></span><span id="civ-name"></span></div>
    <div class="h-stat"><span>Turn</span><b id="turn">1</b></div>
    <div class="h-stat" id="treasury-stat" title="Treasury, and what it gains or loses each turn"><span>Treasury</span><b id="treasury"></b></div>
    <button id="research" class="h-stat stat-button" title="The technology tree (T)"><span>Research</span><b id="research-name">—</b></button>
    <div class="science h-stat" title="Share of commerce sent to research. The rest becomes gold.">
      <span>Science</span>
      <b><button id="rate-down" class="step" title="Less science, more gold">−</button><em id="rate">50%</em><button id="rate-up" class="step" title="More science, less gold">+</button></b>
    </div>
    <div class="h-stat" title="Culture: the empire's total, and what it adds each turn"><span>Culture</span><b id="culture">0</b></div>
    <div class="sep"></div>
    <button id="end-turn" class="h-btn-primary">End turn</button>
    <button id="toggle-panel" class="h-btn menu-btn" title="Settings and saves" aria-label="Settings and saves"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M3 6h14M3 10h14M3 14h14"/></svg></button>
  </header>

  <nav class="rail" aria-label="Screens">
    <button class="rail-btn on" id="rail-map" title="The map (Esc)">${icon('map')}Map</button>
    <button class="rail-btn" id="cities" title="Every city at a glance (F1)">${icon('cities')}Cities</button>
    <button class="rail-btn" id="rail-research" title="The technology tree (T)">${icon('research')}Research</button>
    <button class="rail-btn" id="rivals" title="Rivals: war and peace (F2)">${icon('rivals')}<span id="rivals-label">Rivals</span></button>
    <button class="rail-btn" id="government" title="Government (F3)">${icon('government')}<span id="government-label">Rule</span></button>
    <button class="rail-btn" id="score" title="Score and history (F4)">${icon('score')}Score</button>
    <div class="rail-gap"></div>
    <button class="rail-btn" id="rail-saves" title="Saves and settings">${icon('saves')}Saves</button>
  </nav>

  <div class="stage">
    <canvas id="map"></canvas>
    <div class="tooltip" id="tooltip" hidden></div>
    <div class="report h-panel" id="report" hidden></div>
    <div class="hint" id="hint">Left-click selects · right-click moves (shift: whole stack) · B found city · O open city · T research · F1–F4 screens · K bombard · E escort · L board · P 3D view · Enter end turn</div>
  </div>

  <aside class="side h-panel" id="selection-panel">
    <section id="unit-section">
      <div class="side-head"><span class="h-chip" id="unit-chip"></span><div><span class="h-sub" id="unit-sub">Unit</span><div class="h-title" id="unit-title">Nothing selected</div></div></div>
      <dl class="h-rows" id="unit-readout"></dl>
      <div class="side-label h-sub">Orders</div>
      <div class="controls act">
        <button id="cmd-found" class="h-btn-primary">Found city <span class="h-key">B</span></button>
        <button id="cmd-fortify" class="h-btn">Fortify <span class="h-key">F</span></button>
        <button id="cmd-sleep" class="h-btn">Sleep <span class="h-key">S</span></button>
        <button id="cmd-skip" class="h-btn">Skip <span class="h-key">Space</span></button>
        <button id="cmd-cancel" class="h-btn">Cancel</button>
      </div>
      <div class="controls act" id="unit-actions"></div>
      <div class="controls act" id="work-actions"></div>
    </section>
    <section id="city-section">
      <div class="side-head"><span class="h-chip" id="city-chip"></span><div><span class="h-sub">City</span><div class="h-title" id="city-title">No city</div></div></div>
      <dl class="h-rows" id="city-readout"></dl>
      <div class="controls">
        <button id="open-city" class="h-btn-primary wide">Open city screen <span class="h-key">O</span></button>
      </div>
    </section>
  </aside>

  <aside class="minimap h-panel" id="minimap"></aside>

  <aside class="side settings h-panel" id="panel" hidden>
    <div class="side-head"><div><span class="h-sub">Game</span><div class="h-title">Saves and settings</div></div><button class="h-btn close" id="close-panel" aria-label="Close">×</button></div>
    <div class="controls"><button id="new-game" class="h-btn-primary wide" title="Start a new game">New game</button></div>

    <h2>Saves</h2>
    <div class="controls">
      <select id="slot"></select>
      <button id="save" class="h-btn">Save</button>
      <button id="load" class="h-btn">Load</button>
      <button id="delete" class="h-btn">Delete</button>
    </div>
    <div class="controls">
      <select id="autoslot"></select>
      <button id="load-auto" class="h-btn wide">Load autosave</button>
    </div>
    <div class="log" id="log"></div>

    <h2>Map layers</h2>
    <div class="toggles">
      <label><input type="checkbox" id="layer-grid" checked /> Grid</label>
      <label><input type="checkbox" id="layer-rivers" checked /> Rivers</label>
      <label><input type="checkbox" id="layer-resources" checked /> Resources</label>
      <label><input type="checkbox" id="layer-fog" checked /> Fog of war</label>
      <label><input type="checkbox" id="layer-borders" checked /> Borders</label>
      <label title="The angled, raised view; untick for the flat map (P)"><input type="checkbox" id="layer-dimetric" /> 3D view</label>
    </div>

    <h2>World</h2>
    <dl class="h-rows" id="world-readout"></dl>
    <div class="hud" id="hud"></div>

    <h2>New world <span class="dev">dev only</span></h2>
    <div class="field"><label for="seed">Seed</label><input id="seed" value="meridian-lantern-04" size="16" /></div>
    <div class="field"><label for="size">Size</label><select id="size"></select></div>
    <div class="field"><label for="civs">Rivals</label><select id="civs"></select></div>
    <div class="controls">
      <button id="regen" class="h-btn">Regenerate</button>
      <button id="random-seed" class="h-btn">Random</button>
    </div>

    <h2>Generation <span class="dev">dev only</span></h2>
    <div id="sliders"></div>
    <button id="reset-params" class="h-btn">Reset parameters</button>
  </aside>
`;

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

// The minimap jumps the view to wherever it is clicked (ADR-0077).
const minimap = createMinimap($('minimap'), (col, row) => {
  centreOn(camera, engine.state.map, col, row);
  needsRedraw = true;
});
let minimapVersion = -1;
let minimapState: unknown = null;

const seedInput = $<HTMLInputElement>('seed');
const sizeSelect = $<HTMLSelectElement>('size');
const civsSelect = $<HTMLSelectElement>('civs');
const slotSelect = $<HTMLSelectElement>('slot');
const autoSelect = $<HTMLSelectElement>('autoslot');
const logEl = $<HTMLDivElement>('log');
const canvas = $<HTMLCanvasElement>('map');
const tooltip = $<HTMLDivElement>('tooltip');
const ctx = canvas.getContext('2d');

const overrides: MapgenOverrides = {};
/**
 * Which view the player last chose, kept per browser. The 3D view is the default
 * (ADR-0056); storage can be unavailable or empty, which simply means the default.
 */
const VIEW_KEY = 'epoch.view';
function rememberedView3d(): boolean {
  try {
    return window.localStorage.getItem(VIEW_KEY) !== 'flat';
  } catch {
    return true;
  }
}
function rememberView(dimetric: boolean): void {
  try {
    window.localStorage.setItem(VIEW_KEY, dimetric ? '3d' : 'flat');
  } catch {
    // Not remembered; the next visit opens in 3D.
  }
}

const layers = {
  showGrid: true,
  showRivers: true,
  showResources: true,
  showFog: true,
  showBorders: true,
  showDimetric: rememberedView3d(),
};
const keysDown = new Set<string>();

let hover: { col: number; row: number } | null = null;
let pointer: { x: number; y: number } | null = null;
let needsRedraw = true;
let lastFrame = performance.now();
let smoothedFps = 60;
let lastTilesDrawn = 0;
let lastFrameMs = 0;

/** Selection lives here, not in GameState. See the file header. */
let selectedUnitId: number | null = null;
let selectedCityId: number | null = null;
/** Recomputed when the game changes rather than every frame — it walks every city. */
let idleCityIds: Set<number> = new Set();
let reachable: Set<number> | null = null;

const citiesScreen = createCitiesScreen({
  getEngine: () => engine,
  onOpenCity: (cityId) => {
    citiesScreen.close();
    selectCity(cityId);
    cityScreen.open(cityId);
  },
  onGoToCity: (cityId) => {
    const city = engine.state.cities[cityId];
    if (!city) return;
    selectCity(cityId);
    centreOn(camera, engine.state.map, city.col, city.row);
    needsRedraw = true;
  },
});

const techScreen = createTechScreen({
  getEngine: () => engine,
  onChanged: (message, kind) => {
    if (message) log(message, kind ?? 'note');
    refreshTurnDisplay();
  },
});

const dealScreen = createDealScreen({
  getEngine: () => engine,
  onChanged: () => {
    refreshAfterCommand();
    if (rivalsScreen.isOpen) rivalsScreen.refresh();
  },
  log,
});

const rivalsScreen = createRivalsScreen({
  getEngine: () => engine,
  confirmWar: (civId) => askWar([civId]),
  talk: (civId) => dealScreen.open(civId),
  peace: (civId) => dealScreen.openPeace(civId),
  onChanged: () => {
    refreshAfterCommand();
  },
  log,
});

/**
 * The civilisation and kind of world of the game being played, so the dev panel's
 * Regenerate keeps them (ADR-0062). Unset until the new-game screen is used.
 */
let chosenSetup: { civType?: string; mapType?: string } = {};

const newGameScreen = createNewGameScreen({
  getContent: () => engine.content,
  start: (setup) => {
    seedInput.value = setup.seed;
    sizeSelect.value = setup.size;
    civsSelect.value = String(setup.civs);
    chosenSetup = { civType: setup.civType, mapType: setup.mapType };
    generate(setup.size);
    const civ = engine.state.civs[engine.activeCiv];
    if (civ) log(`You lead ${civ.name}. Found your first city where the settler stands, or look for better ground.`, 'note');
  },
  latestAutosave: async () => {
    const saves = await listAutosaves(engine.content.rules.autosave.slots);
    return saves.length ? Math.max(...saves.map((s) => s.turn)) : null;
  },
  continueLatest: () => {
    void listAutosaves(engine.content.rules.autosave.slots).then(async (saves) => {
      const newest = saves.reduce<(typeof saves)[number] | null>((best, s) => (!best || s.turn > best.turn ? s : best), null);
      const json = newest ? await readAutosave(newest.index) : null;
      if (json) adoptLoaded(json, 'Continued from the autosave');
      else log('That autosave is gone.', 'err');
    });
  },
});
$('new-game').addEventListener('click', () => newGameScreen.open());

const scoreScreen = createScoreScreen({
  getEngine: () => engine,
  newGame: () => newGameScreen.open(),
  loadGame: () => {
    $('load').focus();
    log('Choose a slot in the panel and press Load.', 'note');
  },
});

const governmentScreen = createGovernmentScreen({
  getEngine: () => engine,
  confirm: (title, body) => confirmDialog({ title, body, confirm: 'Begin the revolution', cancel: 'Not now' }),
  onChanged: () => {
    refreshAfterCommand();
    showReport();
  },
  log,
});

/** Ask before going to war. War cannot be taken back for a while, so it is asked plainly. */
function askWar(civIds: number[]): Promise<boolean> {
  const names = civIds.map((id) => engine.state.civs[id]?.name ?? 'them').join(' and ');
  const ours = militaryStrength(engine.state, engine.content, engine.activeCiv);
  const theirs = civIds.reduce((sum, id) => sum + militaryStrength(engine.state, engine.content, id), 0);
  // A treaty still running is broken by war, and everyone hears of it (ADR-0060).
  const treaty = Math.max(0, ...civIds.map((id) => treatyLeft(engine.state, engine.activeCiv, id)));
  return confirmDialog({
    title: `War with ${names}?`,
    body:
      `You are at peace with ${names}. Attacking means declaring war.\n` +
      `Your army's strength is ${ours.toFixed(1)}; theirs is ${theirs.toFixed(1)}.\n` +
      (treaty > 0
        ? `This breaks your peace treaty, which has ${treaty} turn${treaty === 1 ? '' : 's'} to run. Every civilisation will trust your word less.\n`
        : '') +
      `Once peace is made again, neither side can declare war for ${engine.content.rules.diplomacy.truceTurns} turns.`,
    confirm: 'Declare war',
    cancel: 'Stay at peace',
    danger: true,
  });
}

const cityScreen = createCityScreen({
  getEngine: () => engine,
  onChanged: (message, kind) => {
    if (message) log(message, kind ?? 'note');
    // Queueing something in the city screen stops that city being idle, and the
    // end-turn button is where the player is told. Without this it stayed stale until
    // some unrelated command happened to refresh it.
    idleCityIds = new Set(engine.idleCities().map((c) => c.id));
    refreshCityReadout();
    refreshTurnDisplay();
    refreshReadout('—');
    needsRedraw = true;
  },
});

/**
 * Show what happened on the turn just ended.
 *
 * The log in the side panel was never enough: it scrolls, it is easy to miss, and it
 * rolls over after sixty lines — which is roughly one turn of a busy empire. This
 * stays on screen until the next end turn, and every line is a link to the thing it is
 * about.
 */
function showReport(): void {
  const notices = engine.lastReport();
  const idle = engine.idleCities();
  const researchIdle = engine.researchIdle();
  const panel = $('report');

  if (notices.length === 0 && idle.length === 0 && !researchIdle) {
    panel.hidden = true;
    return;
  }

  const lines = notices.map((notice, i) => {
    // A peace offer is the one notice that is a question; it gets its answer buttons
    // right there, while the offer is still open.
    const open = notice.offerFrom !== undefined && engine.state.relations.some(
      (r) => r.war && r.peaceOfferedBy === notice.offerFrom && (r.a === engine.activeCiv || r.b === engine.activeCiv),
    );
    const buttons = open
      ? `<span class="offer"><button data-accept="${notice.offerFrom}" class="primary">Accept</button> <button data-refuse="${notice.offerFrom}">Refuse</button></span>`
      : '';
    return `<div class="report-line ${notice.important ? 'important' : ''}" data-notice="${i}">${escapeHtml(notice.text)}${buttons}</div>`;
  });

  for (const city of idle) {
    lines.push(
      `<div class="report-line idle" data-idle="${city.id}">${escapeHtml(city.name)} is building nothing — its production is being wasted</div>`,
    );
  }

  if (researchIdle) {
    lines.push(`<div class="report-line idle" data-research="1">Your scholars have nothing to study — choose what to research</div>`);
  }

  panel.innerHTML =
    `<div class="report-head"><span>News <span class="h-muted">· turn ${engine.state.meta.turn - 1}</span></span><button id="report-close" title="Dismiss">×</button></div>` +
    lines.join('');
  panel.hidden = false;

  $('report-close').addEventListener('click', () => (panel.hidden = true));

  for (const line of panel.querySelectorAll<HTMLElement>('[data-notice]')) {
    const notice = notices[Number(line.dataset['notice'])];
    if (!notice) continue;
    line.addEventListener('click', () => {
      if (notice.cityId !== undefined) {
        const city = engine.state.cities[notice.cityId];
        if (city) {
          selectCity(city.id);
          centreOn(camera, engine.state.map, city.col, city.row);
          needsRedraw = true;
        }
      } else if (notice.techId !== undefined) {
        techScreen.open(notice.techId);
      } else if (notice.offerFrom !== undefined) {
        rivalsScreen.open();
      } else if (notice.at !== undefined) {
        const col = notice.at % engine.state.map.width;
        centreOn(camera, engine.state.map, col, (notice.at - col) / engine.state.map.width);
        needsRedraw = true;
      } else if (notice.unitId !== undefined) {
        const unit = engine.state.units[notice.unitId];
        if (unit) {
          select(unit.id);
          centreOn(camera, engine.state.map, unit.col, unit.row);
          needsRedraw = true;
        }
      }
    });
  }

  for (const button of panel.querySelectorAll<HTMLButtonElement>('button[data-accept], button[data-refuse]')) {
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const accept = button.dataset['accept'] !== undefined;
      const from = Number(button.dataset['accept'] ?? button.dataset['refuse']);
      const result = engine.apply({ t: 'AnswerPeace', from, accept });
      if (result.ok) {
        const name = engine.state.civs[from]?.name ?? 'them';
        log(accept ? `Peace with ${name}.` : `You refuse ${name}'s offer of peace.`, accept ? 'ok' : 'note');
        button.parentElement?.remove();
        refreshAfterCommand();
      } else log(ERROR_TEXT[result.error], 'err');
    });
  }

  for (const line of panel.querySelectorAll<HTMLElement>('[data-research]')) {
    line.addEventListener('click', () => techScreen.open());
  }

  for (const line of panel.querySelectorAll<HTMLElement>('[data-idle]')) {
    line.addEventListener('click', () => {
      const cityId = Number(line.dataset['idle']);
      selectCity(cityId);
      cityScreen.open(cityId);
    });
  }
}

function log(message: string, kind: 'ok' | 'err' | 'note' = 'note'): void {
  const line = document.createElement('div');
  line.className = kind;
  line.textContent = `${new Date().toLocaleTimeString()}  ${message}`;
  logEl.prepend(line);
  while (logEl.childElementCount > 60) logEl.lastElementChild?.remove();
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

function readout(target: HTMLElement, rows: Array<[string, string]>): void {
  target.innerHTML = rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join('');
}

// ─── selection ─────────────────────────────────────────────────────────────────

function selectedUnit(): Unit | null {
  if (selectedUnitId === null) return null;
  return engine.state.units[selectedUnitId] ?? null;
}

function selectedCity(): City | null {
  if (selectedCityId === null) return null;
  return engine.state.cities[selectedCityId] ?? null;
}

/** Every tile within `range` steps of `from`, not counting `from` itself. */
function tilesWithin(from: number, range: number): Set<number> {
  const seen = new Set<number>([from]);
  let ring = [from];
  for (let step = 0; step < range; step++) {
    const next: number[] = [];
    for (const tile of ring) {
      neighborsByDirection(engine.state.map, tile, adjacency);
      for (const n of adjacency) if (n !== -1 && !seen.has(n)) (seen.add(n), next.push(n));
    }
    ring = next;
  }
  seen.delete(from);
  return seen;
}

/** Recompute the movement overlay. Only ever for the selected unit, so it is cheap. */
function refreshReachable(): void {
  const unit = selectedUnit();
  if (!unit || unit.owner !== engine.activeCiv) {
    reachable = null;
    return;
  }
  const guns = unitDef(engine.content, unit).bombard;
  if (bombarding && guns) {
    reachable = tilesWithin(unitIndex(engine.state.map, unit), guns.range);
    return;
  }
  // An aircraft's reach is where it can strike or rebase (ADR-0065).
  const flight = unitDef(engine.content, unit).airRange;
  if (flight !== null) {
    reachable = unit.movesLeft > 0 ? tilesWithin(unitIndex(engine.state.map, unit), flight) : null;
    bombarding = false;
    return;
  }
  bombarding = false;
  // The same route rules as a goto: rivals, harbours, zones of control.
  const options = pathOptions(engine.state, engine.content, unit, null);
  if (!layers.showFog) options.vision = null;
  const tiles = reachableTiles(
    options,
    unitIndex(engine.state.map, unit),
    unit.movesLeft,
  );
  reachable = new Set(tiles.keys());
}

/**
 * Choosing a target for the selected unit's guns (ADR-0064): the overlay shows its
 * range, the tooltip what a shot would do, and a right-click fires.
 */
let bombarding = false;
/** Choosing which ship the selected warship escorts (ADR-0067). */
let escorting = false;

function select(unitId: number | null): void {
  if (unitId !== selectedUnitId) {
    bombarding = false;
    escorting = false;
  }
  selectedUnitId = unitId;
  refreshReachable();
  refreshUnitReadout();
  needsRedraw = true;
}

function selectCity(cityId: number | null): void {
  selectedCityId = cityId;
  refreshCityReadout();
  needsRedraw = true;
}

function refreshCityReadout(): void {
  const city = selectedCity();
  const target = $('city-readout');
  if (!city) {
    readout(target, []);
    showSide();
    return;
  }
  $('city-title').textContent = city.name;
  $('city-chip').style.background = engine.state.civs[city.owner]?.colour ?? 'transparent';

  const content = engine.content;
  const yields = cityYield(engine.state, content, city);
  const surplus = foodSurplus(engine.state, content, city);
  const threshold = growthThreshold(content, city.population);

  let growth: string;
  if (isAtSizeCap(content, city)) growth = capText(content, city);
  else if (surplus > 0) growth = `${Math.ceil((threshold - city.foodStore) / surplus)} turns`;
  else if (surplus < 0) growth = 'starving';
  else growth = 'not growing';

  const head = city.queue[0];
  const building = head
    ? `${itemName(content, head)} — ${city.productionStore} of ${itemCost(content, head, engine.state.civs[city.owner])}`
    : 'nothing';

  const net = effectiveYield(engine.state, content, city);
  const drag = dragFraction(engine.state, content, city);
  const feeling = mood(engine.state, content, city);

  readout(target, [
    ['size', `${city.population} of ${sizeCap(content, city)}`],
    ['food', `${yields.food} grown · ${city.population * content.rules.cities.foodPerCitizen} eaten · ${surplus >= 0 ? '+' : ''}${surplus}`],
    ['store', `${city.foodStore} of ${threshold}`],
    ['growth', growth],
    ['production', feeling.disorder ? `${yields.production} — none of it, in disorder` : `${net.production} per turn`],
    ['building', feeling.disorder ? 'nothing while in disorder' : building],
    ['gold', drag > 0 ? `${net.gold} of ${yields.gold} reaches the treasury` : String(net.gold)],
    ['distance', drag > 0 ? `${Math.round(drag * 100)}% lost on the road to the capital` : 'the capital'],
    ['mood', feeling.disorder ? `in disorder — ${feeling.discontented} discontented` : `${feeling.content} content · ${feeling.discontented} discontented`],
    ['culture', cultureLine(engine.state, content, city)],
    ['founded', `turn ${city.foundedTurn}`],
  ]);
  showSide();
}

/** Move the selection to the next unit still waiting for orders, and look at it. */
function cycleUnit(): void {
  const waiting = engine.awaitingOrders();
  if (waiting.length === 0) {
    log('Every unit has its orders.', 'note');
    return;
  }
  const current = waiting.findIndex((u) => u.id === selectedUnitId);
  const next = waiting[(current + 1) % waiting.length]!;
  select(next.id);
  centreOn(camera, engine.state.map, next.col, next.row);
}

/**
 * Grey out actions the selected unit cannot take, and say why on hover.
 *
 * The reason is asked of the engine rather than worked out here. That is the whole
 * point of splitting validation from execution (ADR-0015): there is one copy of the
 * rule, and the button and the command can never disagree about it.
 */
function refreshActionButtons(): void {
  const unit = selectedUnit();
  const button = $<HTMLButtonElement>('cmd-found');

  if (!unit) {
    button.disabled = true;
    button.title = 'No unit selected.';
    return;
  }
  const refusal = engine.check({ t: 'FoundCity', unitId: unit.id });
  button.disabled = refusal !== null;
  button.title = refusal === null ? 'Found a city here' : ERROR_TEXT[refusal];
}

/**
 * Buttons for whatever the selected worker could build where it stands.
 *
 * Built from the engine's own answer rather than from a list here, so a worker is
 * never offered something the command would refuse.
 */
function refreshWorkActions(): void {
  const panel = $('work-actions');
  const unit = selectedUnit();
  if (!unit) {
    panel.innerHTML = '';
    return;
  }

  // What is already being built here by this worker is not offered again: choosing it
  // a second time used to start the work over from nothing.
  // Only workers build; a soldier standing on farmland was being offered the farm.
  if (!canBuildImprovements(engine.content, unit) || unit.owner !== engine.activeCiv) {
    panel.innerHTML = '';
    return;
  }
  // Automation first: the worker chooses its own jobs until given another order (ADR-0077).
  const automate = unit.automated
    ? `<button id="cmd-automate" class="primary wide" title="This worker chooses its own jobs. Any other order takes it off automation.">Automated · click to stop</button>`
    : `<button id="cmd-automate" class="wide" title="Let this worker choose its own jobs each turn, replacing improvements where a city would gain">Automate worker</button>`;
  const wireAutomate = (): void => {
    panel.querySelector('#cmd-automate')?.addEventListener('click', () => {
      const result = unit.automated
        ? engine.apply({ t: 'CancelOrders', unitId: unit.id })
        : engine.apply({ t: 'Automate', unitId: unit.id });
      if (!result.ok) log(ERROR_TEXT[result.error], 'err');
      else log(unit.automated ? 'The worker will choose its own jobs.' : 'The worker waits for your orders.', 'ok');
      refreshAfterCommand();
    });
  };
  const underWay = unit.orders?.kind === 'build' ? unit.orders.improvement : -1;
  const options = buildableHere(engine.content, engine.state, unit).filter((i) => i.id !== underWay);
  if (options.length === 0) {
    panel.innerHTML = automate;
    wireAutomate();
    return;
  }

  const at = unitIndex(engine.state.map, unit);
  const current = engine.content.improvements.get(engine.state.map.improvement[at]!);
  const yields = (y: { food: number; production: number; gold: number }): string =>
    [y.food && `+${y.food} food`, y.production && `+${y.production} production`, y.gold && `+${y.gold} gold`]
      .filter(Boolean)
      .join(', ') || 'nothing';

  panel.innerHTML = automate + options
    .map((improvement, i) => {
      // Say plainly when a choice replaces what is there. A road never does — it lies
      // alongside (ADR-0055).
      const replaces = !improvement.route && current.key !== 'none' ? current : null;
      const effect = improvement.route
        ? `${yields(improvement.yields)}; units move along roads three times as fast`
        : replaces
          ? `${yields(improvement.yields)} instead of the ${replaces.name}'s ${yields(replaces.yields)}`
          : yields(improvement.yields);
      const title = `${improvement.description}\n${effect}\n${improvement.turnsToBuild} worker-turns`;
      const label = replaces ? `${improvement.name} (replaces ${replaces.name})` : improvement.name;
      return `<button data-build-improvement="${i}" class="${replaces ? 'replaces' : ''}" title="${escapeHtml(title)}">${escapeHtml(label)}</button>`;
    })
    .join('');

  wireAutomate();
  for (const button of panel.querySelectorAll<HTMLButtonElement>('button[data-build-improvement]')) {
    button.addEventListener('click', () => {
      const improvement = options[Number(button.dataset['buildImprovement'])];
      if (!improvement) return;
      const result = engine.apply({ t: 'BuildImprovement', unitId: unit.id, improvement: improvement.id });
      if (!result.ok) log(ERROR_TEXT[result.error], 'err');
      else log(`Work started on a ${improvement.name}.`, 'ok');
      refreshAfterCommand();
    });
  }
}

function refreshUnitReadout(): void {
  refreshActionButtons();
  refreshWorkActions();
  const unit = selectedUnit();
  const target = $('unit-readout');
  if (!unit) {
    readout(target, []);
    showSide();
    return;
  }

  const def = unitDef(engine.content, unit);
  const owner = engine.state.civs[unit.owner];
  $('unit-title').textContent = def.name;
  $('unit-sub').textContent = `${owner?.name ?? 'Unit'} · ${rankOf(engine.content, unit).name}`;
  $('unit-chip').style.background = owner?.colour ?? 'transparent';
  const scale = engine.content.rules.movement.scale;
  let orders: string;
  if (unit.automated && unit.orders?.kind !== 'build' && unit.orders?.kind !== 'goto') orders = 'automated';
  else if (unit.orders === null) orders = 'awaiting orders';
  else if (unit.orders.kind === 'goto') orders = `moving (${unit.orders.path.length - unit.orders.step} tiles to go)`;
  else if (unit.orders.kind === 'build') {
    const improvement = engine.content.improvements.get(unit.orders.improvement);
    const left = turnsRemaining(engine.content, engine.state, unit);
    orders = `building a ${improvement.name}${left === null ? '' : ` · ${left} turn${left === 1 ? '' : 's'} left`}`;
  } else if (unit.orders.kind === 'escort') {
    const guarded = engine.state.units[unit.orders.target];
    orders = guarded ? `escorting the ${engine.content.units.get(guarded.type).name}` : 'escorting';
  } else orders = unit.orders.kind;

  if (unit.automated && orders !== 'automated') orders = `automated · ${orders}`;
  readout(target, [
    ['health', `${unit.hp} of ${maxHp(engine.content, unit)}`],
    ['strength', `attack ${def.attack} · defence ${def.defence}`],
    def.airRange !== null
      ? ['mission', unit.movesLeft > 0 ? `ready · range ${def.airRange} — right-click to strike or rebase` : 'flown this turn']
      : ['moves', `${(unit.movesLeft / scale).toFixed(2).replace(/\.?0+$/, '')} of ${def.movement}`],
    ['sight', String(def.sight)],
    ['at', `${unit.col}, ${unit.row}`],
    ['orders', orders],
    ...(def.capacity > 0 ? ([['carrying', `${cargoOf(engine.state, unit).length} of ${def.capacity}`]] as Array<[string, string]>) : []),
    ...(def.airCapacity > 0
      ? ([['aircraft', `${cargoOf(engine.state, unit).filter((u) => unitDef(engine.content, u).domain === 'air').length} of ${def.airCapacity} on deck`]] as Array<[string, string]>)
      : []),
    ...(unit.aboard !== NONE ? ([['aboard', engine.content.units.get(engine.state.units[unit.aboard]?.type ?? 0).name]] as Array<[string, string]>) : []),
  ]);
  refreshUnitActions(unit);
  showSide();
}

/** The selection panel shows the unit and the city selected, and hides when there are neither. */
function showSide(): void {
  const unit = selectedUnit() !== null;
  const city = selectedCity() !== null;
  $('unit-section').hidden = !unit;
  $('city-section').hidden = !city;
  $('selection-panel').hidden = !unit && !city;
}

/**
 * Upgrade, board and go ashore — the three things a unit can do that depend on where
 * it stands. Each button is there only when the engine would accept it, and says what
 * it costs.
 */
function refreshUnitActions(unit: Unit | null): void {
  const panel = $('unit-actions');
  if (!unit || unit.owner !== engine.activeCiv) {
    panel.innerHTML = '';
    return;
  }
  const buttons: string[] = [];
  const civ = engine.state.civs[unit.owner];
  // What it could become here, with this city's strategic resources; failing that, what it
  // could become anywhere — shown greyed, with the reason (ADR-0063).
  const has = resourcesAt(engine.state, resourceAccess(engine.state, engine.content, unit.owner), unitIndex(engine.state.map, unit));
  const target = civ
    ? (upgradeTarget(engine.content, civ, unitDef(engine.content, unit), has) ?? upgradeTarget(engine.content, civ, unitDef(engine.content, unit)))
    : null;
  if (target) {
    const cost = upgradeCost(engine.content, unitDef(engine.content, unit), target);
    const refusal = engine.check({ t: 'UpgradeUnit', unitId: unit.id });
    buttons.push(
      `<button id="cmd-upgrade" ${refusal ? 'disabled' : ''} title="${escapeHtml(refusal ? ERROR_TEXT[refusal] : `Refit as a ${target.name} (U)`)}">` +
        `Upgrade to ${escapeHtml(target.name)} · ${cost}g</button>`,
    );
  }
  const guns = unitDef(engine.content, unit).bombard;
  if (guns) {
    buttons.push(
      `<button id="cmd-bombard" class="${bombarding ? 'primary' : ''}" ${unit.movesLeft <= 0 ? 'disabled' : ''} ` +
        `title="Fire ${guns.shots} shots at an enemy up to ${guns.range} tile${guns.range === 1 ? '' : 's'} away — hurts, never kills (K)">` +
        `${bombarding ? 'Choose a target…' : 'Bombard'}</button>`,
    );
  }
  if (def0IsWarship(unit)) {
    buttons.push(
      `<button id="cmd-escort" class="${escorting ? 'primary' : ''}" title="Follow one of your ships and defend it, every turn (E)">${escorting ? 'Choose a ship…' : 'Escort'}</button>`,
    );
  }
  if (engine.check({ t: 'Board', unitId: unit.id }) === null) buttons.push('<button id="cmd-board" title="Go aboard the ship in port (L)">Board ship</button>');
  if (engine.check({ t: 'Unload', unitId: unit.id }) === null) buttons.push('<button id="cmd-unload" title="Leave the ship, staying in port (L)">Go ashore</button>');
  panel.innerHTML = buttons.join('');
  panel.querySelector('#cmd-upgrade')?.addEventListener('click', upgradeSelected);
  panel.querySelector('#cmd-bombard')?.addEventListener('click', toggleBombard);
  panel.querySelector('#cmd-escort')?.addEventListener('click', toggleEscort);
  panel.querySelector('#cmd-board')?.addEventListener('click', () => boardOrUnload());
  panel.querySelector('#cmd-unload')?.addEventListener('click', () => boardOrUnload());
}

function def0IsWarship(unit: Unit): boolean {
  const def = unitDef(engine.content, unit);
  return def.domain === 'sea' && def.defence > 0;
}

function toggleEscort(): void {
  const unit = selectedUnit();
  if (!unit || unit.owner !== engine.activeCiv || !def0IsWarship(unit)) return;
  escorting = !escorting;
  bombarding = false;
  if (escorting) log('Escort: right-click the ship to guard. Esc to cancel.', 'note');
  refreshUnitReadout();
  needsRedraw = true;
}

/** Order the selected warship to escort a ship of ours on this tile: a transport or carrier first. */
function escortAt(index: number): void {
  const unit = selectedUnit();
  if (!unit) return;
  const ours = unitsOf(engine.state, engine.activeCiv)
    .filter((u) => u.id !== unit.id && unitIndex(engine.state.map, u) === index && unitDef(engine.content, u).domain === 'sea')
    .sort((a, b) => {
      const cargo = (u: Unit): number => (unitDef(engine.content, u).capacity > 0 || unitDef(engine.content, u).airCapacity > 0 ? 0 : 1);
      return cargo(a) - cargo(b) || a.id - b.id;
    });
  escorting = false;
  if (ours.length === 0) {
    log('Right-click one of your ships to escort it.', 'err');
    refreshUnitReadout();
    return;
  }
  const result = engine.apply({ t: 'Escort', unitId: unit.id, target: ours[0]!.id });
  if (!result.ok) log(ERROR_TEXT[result.error], 'err');
  else log(`Escorting the ${engine.content.units.get(ours[0]!.type).name}.`, 'ok');
  refreshAfterCommand();
}

function toggleBombard(): void {
  const unit = selectedUnit();
  const guns = unit ? unitDef(engine.content, unit).bombard : null;
  if (!unit || !guns || unit.owner !== engine.activeCiv) return;
  if (!bombarding && unit.movesLeft <= 0) {
    log(ERROR_TEXT.NoMovesLeft, 'err');
    return;
  }
  bombarding = !bombarding;
  if (bombarding) log(`Bombard: right-click an enemy within ${guns.range} tile${guns.range === 1 ? '' : 's'}. Esc to cancel.`, 'note');
  refreshReachable();
  refreshUnitReadout();
  needsRedraw = true;
}

/** Send the selected aircraft on a mission: strike what is there, or rebase if it is ours. */
function flyTo(index: number): void {
  const unit = selectedUnit();
  if (!unit) return;
  const name = engine.content.units.get(unit.type).name;
  const result = engine.apply({ t: 'AirMission', unitId: unit.id, target: index });
  if (!result.ok) {
    log(ERROR_TEXT[result.error], 'err');
    return;
  }
  for (const event of result.value) {
    if (event.t === 'AircraftRebased') log(unit.aboard !== NONE ? `${name} landed on the carrier.` : `${name} rebased.`, 'ok');
    else if (event.t === 'AirIntercepted' && event.strikerOwner === engine.activeCiv) {
      log(`Enemy fighters met your ${name}: ${event.strikerHp > 0 ? `it flew on with ${event.strikerHp} health` : 'it went down'}.`, event.strikerHp > 0 ? 'note' : 'err');
    } else if (event.t === 'AntiAirFired' && event.strikerOwner === engine.activeCiv) {
      log(`Anti-aircraft fire: ${event.hits === 0 ? 'missed' : `−${event.damage} health`}.`, event.hits === 0 ? 'note' : 'err');
    } else if (event.t === 'AircraftLost' && event.owner === engine.activeCiv) {
      log(`Your ${engine.content.units.get(event.unitType).name} was lost.`, 'err');
    } else if (event.t === 'CombatResolved' && event.attackerOwner === engine.activeCiv) {
      log(
        event.attackerWon
          ? `Air strike: the ${engine.content.units.get(event.defenderType).name} is destroyed. Your ${name} returns with ${event.attackerHp} health.`
          : `Air strike failed: your ${name} was shot down by the ${engine.content.units.get(event.defenderType).name}.`,
        event.attackerWon ? 'ok' : 'err',
      );
    }
  }
  refreshAfterCommand();
}

/** The tooltip's line about flying the selected aircraft to the hovered tile. */
function airLine(unit: Unit, index: number): string {
  const city = engine.state.cities ? Object.values(engine.state.cities).find((c) => tileIndex(engine.state.map, c.col, c.row) === index) : undefined;
  if (city && city.owner === unit.owner) return `<span class="odds-good">Rebase to ${escapeHtml(city.name)}</span>`;
  const deck = carrierAt(engine.state, engine.content, unit.owner, index);
  if (deck) return `<span class="odds-good">Land on the ${escapeHtml(engine.content.units.get(deck.type).name)}</span>`;
  const refusal = engine.check({ t: 'AirMission', unitId: unit.id, target: index });
  if (refusal) return `<span class="dim">${escapeHtml(ERROR_TEXT[refusal])}</span>`;
  const plan = planStrike(engine.state, engine.content, unit, index);
  if (!plan) return `<span class="dim">${escapeHtml(ERROR_TEXT.NothingToStrike)}</span>`;
  const threats: string[] = [];
  if (plan.interceptor) threats.push(`an enemy ${escapeHtml(engine.content.units.get(plan.interceptor.type).name)} may intercept`);
  if (plan.antiAir > 0) threats.push(`anti-aircraft ${plan.antiAir}% a shot`);
  const pct = Math.round(plan.odds * 100);
  const m = plan.matchup;
  return (
    `<span class="${plan.odds >= 0.5 ? 'odds-good' : 'odds-bad'}">Strike the ${escapeHtml(engine.content.units.get(plan.defender.type).name)}: <b>${pct}%</b> to win</span>` +
    `<span class="dim"> · ${Math.round(plan.lostOnTheWay * 100)}% lost on the way${threats.length ? ` (${threats.join(', ')})` : ''}` +
    ` · attack ${m.attack} vs defence ${m.defence.toFixed(1)} · walls do not count</span>`
  );
}

/** Fire at a tile, while choosing a target. */
function bombardAt(index: number): void {
  const unit = selectedUnit();
  if (!unit) return;
  const result = engine.apply({ t: 'Bombard', unitId: unit.id, target: index });
  if (!result.ok) {
    log(ERROR_TEXT[result.error], 'err');
    return;
  }
  bombarding = false;
  for (const event of result.value) {
    if (event.t !== 'UnitBombarded') continue;
    const name = engine.content.units.get(event.targetType).name;
    log(
      event.damage > 0
        ? `Bombardment: ${event.hits} of ${event.shots} shots hit the ${name} — −${event.damage} health, ${event.targetHp} left.`
        : `Bombardment: all ${event.shots} shots missed the ${name}.`,
      event.damage > 0 ? 'ok' : 'note',
    );
  }
  refreshAfterCommand();
}

function upgradeSelected(): void {
  const unit = selectedUnit();
  if (!unit) return;
  const result = engine.apply({ t: 'UpgradeUnit', unitId: unit.id });
  if (!result.ok) log(ERROR_TEXT[result.error], 'err');
  else {
    for (const event of result.value) {
      if (event.t === 'UnitUpgraded') {
        log(`Upgraded to ${engine.content.units.get(event.to).name} for ${event.cost} gold.`, 'ok');
      }
    }
  }
  refreshAfterCommand();
  refreshTurnDisplay();
}

function boardOrUnload(): void {
  const unit = selectedUnit();
  if (!unit) return;
  const cmd = unit.aboard === NONE ? ({ t: 'Board', unitId: unit.id } as const) : ({ t: 'Unload', unitId: unit.id } as const);
  const result = engine.apply(cmd);
  if (!result.ok) log(ERROR_TEXT[result.error], 'err');
  else log(cmd.t === 'Board' ? 'Aboard — it sails with the ship.' : 'Ashore.', 'ok');
  refreshAfterCommand();
}

// ─── boot ──────────────────────────────────────────────────────────────────────

function boot(): void {
  if (!ctx) {
    app.innerHTML = '<section class="panel"><h2>Canvas 2D is unavailable in this browser.</h2></section>';
    return;
  }
  try {
    generate('standard', true);
  } catch (error) {
    app.innerHTML = `<section class="panel"><h2>Failed to start</h2><pre class="log err">${escapeHtml(
      error instanceof Error ? error.message : String(error),
    )}</pre></section>`;
    return;
  }

  for (const size of engine.content.mapgen.sizes) {
    const option = document.createElement('option');
    option.value = size.key;
    option.textContent = `${size.name} — ${size.width}×${size.height}`;
    if (size.key === engine.content.mapgen.defaults.size) option.selected = true;
    sizeSelect.append(option);
  }
  // How many civilisations share the world. Four is a reasonable default now that
  // there is an AI to run the other three.
  for (let count = 1; count <= engine.content.civilizations.all.length; count++) {
    const option = document.createElement('option');
    option.value = String(count);
    option.textContent = count === 1 ? 'Alone' : `${count} civilisations`;
    if (count === 4) option.selected = true;
    civsSelect.append(option);
  }
  civsSelect.addEventListener('change', () => generate(sizeSelect.value));

  for (let slot = 1; slot <= SLOT_COUNT; slot++) {
    const option = document.createElement('option');
    option.value = String(slot);
    option.textContent = `Slot ${slot}`;
    slotSelect.append(option);
  }

  buildSliders();
  resizeCanvas();
  requestAnimationFrame(frame);
  log('Engine booted. Content validated.', 'ok');
  // Every session opens on the choice of civilisation and world (ADR-0062).
  newGameScreen.open();
}

function generate(size: string, initial = false): void {
  const seed = seedInput.value.trim() || 'default';
  const started = performance.now();
  const created = createEngine({
    seed,
    size,
    mapgen: overrides,
    civs: Number(civsSelect.value) || 4,
    ...chosenSetup,
  });
  engine = created.engine;
  report = created.report;
  const elapsed = performance.now() - started;

  camera = createCamera(engine.state.map, canvas.clientWidth || 1200, canvas.clientHeight || 700);
  hover = null;
  needsRedraw = true;

  // Open on the civilisation, not on the middle of the ocean.
  const first = unitsOf(engine.state, engine.activeCiv)[0];
  if (first) {
    centreOn(camera, engine.state.map, first.col, first.row);
    select(first.id);
  } else {
    select(null);
  }

  cityScreen.close();
  citiesScreen.close();
  techScreen.close();
  $('report').hidden = true;
  idleCityIds = new Set(engine.idleCities().map((c) => c.id));
  selectCity(null);
  refreshReadout(`${elapsed.toFixed(0)}ms`);
  refreshTurnDisplay();

  if (!initial) {
    const problems = validateWorld(engine.state.map, engine.content);
    if (problems.length > 0) log(`"${seed}" — ${problems.length} problem(s): ${problems[0]!.message}`, 'err');
    else log(`Generated "${seed}" in ${elapsed.toFixed(0)}ms → hash ${engine.hash()}`, 'ok');
  }
}

function refreshTurnDisplay(): void {
  const civ = engine.state.civs[engine.activeCiv];
  const waiting = engine.awaitingOrders().length;
  const idle = engine.idleCities().length;
  const era = civ ? engine.content.eras.get(currentEra(engine.content, civ)).name : '';
  $('turn').textContent = `${engine.state.meta.turn}${era ? ` · ${era}` : ''}`;

  // Who you are, under what rule — anarchy shown as the warning it is.
  $('civ-chip').style.background = civ?.colour ?? 'transparent';
  const anarchy = civ && inAnarchy(civ) ? anarchyLeft(engine.state, civ) : null;
  const rule = anarchy !== null ? `Anarchy · ${anarchy} turns` : governmentOf(engine.content, civ).name;
  $('civ-name').textContent = civ ? `${civ.name} · ${rule}` : '';
  $('civ-name').className = anarchy !== null ? 'warn' : '';
  $('government-label').textContent = anarchy !== null ? 'Anarchy' : 'Rule';
  $('government').classList.toggle('warn', anarchy !== null);

  // A city building nothing is worth more of the player's attention than a unit
  // without orders, because it wastes something every single turn rather than once.
  const researchIdle = engine.researchIdle();
  const parts: string[] = [];
  if (idle > 0) parts.push(`${idle} idle ${idle === 1 ? 'city' : 'cities'}`);
  if (researchIdle) parts.push('no research');
  if (waiting > 0) parts.push(`${waiting} waiting`);
  const endTurn = $('end-turn');
  endTurn.innerHTML = parts.length > 0 ? `End turn <small>${escapeHtml(parts.join(' · '))}</small>` : 'End turn';
  endTurn.classList.toggle('warn', idle > 0 || researchIdle);

  const accounts = budget(engine.state, engine.content, engine.activeCiv);
  const el = $('treasury');
  el.textContent = `${civ?.treasury ?? 0} (${accounts.net >= 0 ? '+' : ''}${accounts.net})`;
  el.className = accounts.net < 0 ? 'falling' : 'rising';
  $('treasury-stat').title =
    `Commerce ${accounts.commerce}: ${accounts.science} to research, ${accounts.income} to the treasury` +
    `\nBuildings −${accounts.buildingUpkeep} · units −${accounts.unitUpkeep}` +
    (accounts.freeUnitUpkeep > 0 ? ` (${accounts.freeUnitUpkeep} more supported free by your cities)` : '') +
    (accounts.net < 0 ? '\nThe treasury is draining. At zero, a building is sold.' : '');

  // What the scholars are doing, on the bar, so the player never has to open the tree
  // just to find out.
  const research = $('research-name');
  if (civ && civ.researching !== NONE) {
    const turns = turnsToDiscover(engine.content, civ, accounts.science);
    research.textContent = `${engine.content.technologies.get(civ.researching).name} · ${turns === null ? '—' : `${turns} turn${turns === 1 ? '' : 's'}`}`;
    research.className = '';
  } else {
    research.textContent = 'Choose research';
    research.className = 'warn';
  }

  const cities = citiesOf(engine.state, engine.activeCiv);
  const culture = cities.reduce((n, c) => n + c.culture, 0);
  const gaining = cities.reduce((n, c) => n + culturePerTurn(engine.state, engine.content, c), 0);
  $('culture').textContent = `${culture.toLocaleString('en-GB')} (+${gaining})`;

  const wars = enemiesOf(engine.state, engine.activeCiv);
  $('rivals-label').textContent = wars.length > 0 ? `War (${wars.length})` : 'Rivals';
  $('rivals').classList.toggle('warn', wars.length > 0);
  // The government caps the slider (ADR-0059): what is shown is what is spent.
  const cap = maxScienceRate(engine.state, engine.content, engine.activeCiv);
  $('rate').textContent = `${Math.min(civ?.researchRate ?? 0, cap)}%`;
  $('rate').title = `Your government allows up to ${cap}%`;
  ($('rate-down') as HTMLButtonElement).disabled = !civ || civ.researchRate <= 0;
  ($('rate-up') as HTMLButtonElement).disabled = !civ || civ.researchRate >= cap;
}

function stepScienceRate(direction: 1 | -1): void {
  const civ = engine.state.civs[engine.activeCiv];
  if (!civ) return;
  const rate = civ.researchRate + direction * engine.content.rules.research.rateStep;
  if (engine.apply({ t: 'SetScienceRate', rate }).ok) {
    refreshTurnDisplay();
    techScreen.refresh();
  }
}

function refreshReadout(timing: string): void {
  const map = engine.state.map;
  const content = engine.content;
  const total = tileCount(map);

  const counts = new Map<string, number>();
  for (const id of map.terrain) {
    const key = content.terrain.get(id).key;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const composition = [...counts.entries()]
    .filter(([key]) => !content.terrain.byKey.get(key)!.water)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${k} ${Math.round((n / report.landTiles) * 100)}%`)
    .join(' · ');

  readout($('world-readout'), [
    ['seed', engine.state.meta.seed],
    ['size', `${map.width} × ${map.height} (${total.toLocaleString()} tiles)`],
    ['water', `${Math.round((report.waterTiles / total) * 100)}%`],
    ['land', composition],
    ['water features', `${report.lakeCount} lakes · ${report.riverCount} rivers`],
    ['explored', `${(exploredFraction(engine.state, engine.activeCiv) * 100).toFixed(1)}%`],
    ['generated in', timing],
    ['state hash', engine.hash()],
  ]);
}

function buildSliders(): void {
  const defaults = engine.content.mapgen.defaults as unknown as Record<string, number>;
  $('sliders').innerHTML = TUNABLE.map(
    (t) => `<div class="slider"><label for="p-${t.key}">${t.label}</label>
      <input type="range" id="p-${t.key}" min="${t.min}" max="${t.max}" step="${t.step}" value="${defaults[t.key]}" />
      <output id="o-${t.key}">${defaults[t.key]}</output></div>`,
  ).join('');

  for (const t of TUNABLE) {
    const input = $<HTMLInputElement>(`p-${t.key}`);
    input.addEventListener('input', () => ($(`o-${t.key}`).textContent = input.value));
    input.addEventListener('change', () => {
      (overrides as Record<string, number>)[t.key] = Number(input.value);
      generate(sizeSelect.value);
    });
  }
}

// ─── the frame loop ────────────────────────────────────────────────────────────

let sceneCache: { version: number; viewer: number | null; state: unknown; scene: Scene } | null = null;

/**
 * The scene for the current view, rebuilt only when the game or the viewer changes —
 * the renderer and the hover tooltip both read it, and neither should rebuild it.
 */
function currentScene(): Scene {
  const viewer = layers.showFog ? engine.activeCiv : null;
  if (
    !sceneCache ||
    sceneCache.version !== engine.version ||
    sceneCache.viewer !== viewer ||
    sceneCache.state !== engine.state
  ) {
    sceneCache = { version: engine.version, viewer, state: engine.state, scene: buildScene(engine.state, engine.content, viewer) };
  }
  return sceneCache.scene;
}

/**
 * Redraws only when something has changed, rather than every animation frame.
 *
 * A strategy map is static most of the time: a player reading a tooltip should not
 * be burning a core repainting an identical image sixty times a second. Keyboard
 * panning marks the frame dirty each tick, so held keys still animate smoothly.
 */
function frame(now: number): void {
  const delta = Math.min(100, now - lastFrame);
  lastFrame = now;

  applyKeyboardPan(delta);

  if (needsRedraw && ctx) {
    const unit = selectedUnit();
    const path = unit?.orders?.kind === 'goto' ? unit.orders.path.slice(unit.orders.step) : null;

    const draw = layers.showDimetric ? renderDimetric : renderMap;
    const stats = draw(ctx, currentScene(), engine.content, camera, {
      showGrid: layers.showGrid,
      showRivers: layers.showRivers,
      showResources: layers.showResources,
      showBorders: layers.showBorders,
      hover,
      selectedUnit: selectedUnitId,
      reachable,
      path,
      workedBy: selectedCityId,
      idleCities: idleCityIds,
      resourceSeen: (r) => resourceKnown(engine.content, engine.state.civs[engine.activeCiv], r),
    });
    const scene = currentScene();
    if (minimapVersion !== engine.version || minimapState !== engine.state) {
      minimap.refresh(scene, engine.content);
      minimapVersion = engine.version;
      minimapState = engine.state;
    }
    minimap.drawView(visibleTiles());
    lastTilesDrawn = stats.tilesDrawn;
    lastFrameMs = stats.frameMs;
    // Exponential smoothing: a raw per-frame figure is unreadable.
    smoothedFps = smoothedFps * 0.9 + (1000 / Math.max(1, delta)) * 0.1;
    // Models not yet drawn showed as discs this frame; draw again to fill them in.
    needsRedraw = modelsWaiting();
    updateHud();
  }

  requestAnimationFrame(frame);
}

/** The part of the world on screen, in tile columns and rows, for the minimap's box. */
function visibleTiles(): MinimapView {
  const size = camera.tileSize;
  const colWidth = Math.sqrt(3) * size;
  const rowHeight = 1.5 * size;
  const squash = layers.showDimetric ? SQUASH : 1;
  const halfW = camera.viewportWidth / 2;
  const halfH = camera.viewportHeight / 2 / squash;
  return {
    left: (camera.x - halfW) / colWidth,
    right: (camera.x + halfW) / colWidth,
    top: (camera.y - halfH) / rowHeight,
    bottom: (camera.y + halfH) / rowHeight,
  };
}

function updateHud(): void {
  const where = hover ? `${hover.col}, ${hover.row}` : '—';
  $('hud').textContent =
    `${where}   zoom ${camera.tileSize.toFixed(0)}px   ${lastTilesDrawn} tiles   ${lastFrameMs.toFixed(1)} ms/frame`;
}

const PAN_SPEED = 0.9; // pixels per millisecond at any zoom

/**
 * Arrow keys only.
 *
 * WASD panning was dropped when units arrived: letters are worth far more as unit
 * commands, and a game where "s" both sleeps a unit and pans south is one where the
 * conflict gets worse with every phase.
 */
function applyKeyboardPan(delta: number): void {
  let dx = 0;
  let dy = 0;
  if (keysDown.has('ArrowLeft')) dx -= 1;
  if (keysDown.has('ArrowRight')) dx += 1;
  if (keysDown.has('ArrowUp')) dy -= 1;
  if (keysDown.has('ArrowDown')) dy += 1;
  if (dx === 0 && dy === 0) return;
  panBy(camera, engine.state.map, dx * PAN_SPEED * delta, dy * PAN_SPEED * delta);
  refreshHover();
  needsRedraw = true;
}

// ─── giving orders ─────────────────────────────────────────────────────────────

function report_(result: ReturnType<Engine['apply']>, success?: string): boolean {
  if (!result.ok) {
    log(ERROR_TEXT[result.error], 'err');
    return false;
  }
  if (success) log(success, 'ok');
  return true;
}

/**
 * LEFT-CLICK SELECTS. RIGHT-CLICK MOVES.
 *
 * One click used to do both jobs, and it could not: the handler checked for a city,
 * returned, checked for one of your own units, returned — so a unit could never be
 * sent into your own city or onto a tile holding another of your units. The engine
 * always allowed both; only this function refused.
 *
 * Splitting them across two buttons is the only arrangement where a click's meaning
 * never depends on what happens to be under it.
 */
function clickTile(col: number, row: number): void {
  const index = tileIndex(engine.state.map, col, row);
  if (index === -1) return;

  const here = cityAt(engine.state, engine.state.map, index);
  if (here && here.owner === engine.activeCiv) {
    // Clicking a city that is already selected opens it — the second click on a thing
    // you have already picked is the one people expect to do something.
    if (selectedCityId === here.id) cityScreen.open(here.id);
    selectCity(here.id);
    // Selecting a city does not deselect the units standing in it, so a garrison can
    // still be ordered out while the city panel is up.
    return;
  }

  const mine = unitsOf(engine.state, engine.activeCiv).filter((u) => unitIndex(engine.state.map, u) === index);
  if (mine.length > 0) {
    // Clicking a stack repeatedly walks through it, rather than always picking the first.
    const current = mine.findIndex((u) => u.id === selectedUnitId);
    select(mine[(current + 1) % mine.length]!.id);
    return;
  }

  select(null);
}

/**
 * Send the selected unit — or everything stacked with it, held shift — to a tile.
 *
 * Sending a unit into the dark is allowed, and is how exploring works. The path is
 * routed on what this civilisation knows (ADR-0017), so it heads off hopefully and
 * re-routes when the ground turns out to disagree.
 */
function sendTo(col: number, row: number, wholeStack: boolean): void {
  const index = tileIndex(engine.state.map, col, row);
  if (index === -1) return;

  const unit = selectedUnit();
  if (!unit || unit.owner !== engine.activeCiv) {
    log('Select one of your units first.', 'err');
    return;
  }

  const from = unitIndex(engine.state.map, unit);

  // Right-clicking something hostile next door is an attack (or a question about war),
  // not a journey — a goto never fights.
  const visible = engine.state.vision[engine.activeCiv]?.[index] === VIS_VISIBLE;
  if (visible && isAdjacent(from, index)) {
    const preview = engine.preview(unit.id, index);
    // Stepping a soldier onto water next to it means boarding the ship there.
    const boarding =
      unitDef(engine.content, unit).domain === 'land' && engine.content.terrain.get(engine.state.map.terrain[index]!).water;
    if (preview && (preview.kind !== 'move' || boarding)) {
      void moveOrAttack(unit, index);
      return;
    }
  }

  const moving = wholeStack
    ? unitsOf(engine.state, engine.activeCiv).filter((u) => unitIndex(engine.state.map, u) === from)
    : [unit];

  let sent = 0;
  for (const member of moving) {
    if (engine.apply({ t: 'SetGoto', unitId: member.id, to: index }).ok) sent++;
  }

  if (sent === 0) log(ERROR_TEXT[engine.check({ t: 'SetGoto', unitId: unit.id, to: index }) ?? 'NoPath'], 'err');
  else if (moving.length > 1) log(`${sent} of ${moving.length} units on the move.`, 'ok');

  refreshAfterCommand();
}

const adjacency = new Int32Array(6);
function isAdjacent(from: number, to: number): boolean {
  neighborsByDirection(engine.state.map, from, adjacency);
  return adjacency.includes(to);
}

/** The tooltip's line about bombarding the hovered tile (ADR-0064). */
function bombardLine(unit: Unit, index: number): string {
  const refusal = engine.check({ t: 'Bombard', unitId: unit.id, target: index });
  if (refusal) return `<span class="dim">${escapeHtml(ERROR_TEXT[refusal])}</span>`;
  const b = barrage(engine.state, engine.content, unit, index);
  if (!b) return `<span class="dim">${escapeHtml(ERROR_TEXT.NothingToBombard)}</span>`;
  const name = escapeHtml(engine.content.units.get(b.target.type).name);
  return (
    `<span class="odds-good">Bombard the ${name}: ${b.shots} shots at <b>${Math.round(b.chance * 100)}%</b></span> ` +
    `<span class="dim">· about −${b.expected.toFixed(1)} health of ${b.target.hp} · strength ${b.strength} vs defence ${b.defence.toFixed(1)} · cannot kill</span>`
  );
}

/** The tooltip's line about attacking the hovered tile with the selected unit. */
function attackLine(preview: MovePreview): string | null {
  const civ = (id: number): string => escapeHtml(engine.state.civs[id]?.name ?? '');
  switch (preview.kind) {
    case 'move':
      return null;
    case 'blocked':
      return '<span class="odds-bad">This unit cannot fight what is there</span>';
    case 'zoc':
      return '<span class="odds-bad">Enemy zone of control — cannot step here from where it stands</span>';
    case 'border':
      return `<span class="odds-bad">${civ(preview.civ)}'s border is closed to you — agree open borders, or go to war</span>`;
    case 'capture':
      return `<span class="odds-good">Undefended — walk in to take ${preview.city ? 'the city' : 'it'}</span>`;
    case 'war':
      return preview.truce > 0
        ? `<span class="odds-bad">Truce with ${preview.civs.map(civ).join(', ')} · ${preview.truce} turns</span>`
        : `<span class="odds-bad">At peace with ${preview.civs.map(civ).join(', ')} — attacking means war</span>`;
    case 'attack': {
      const m = preview.matchup;
      const pct = Math.round(m.odds * 100);
      const bonus = m.factors.map((f) => (f.percent === 0 ? escapeHtml(f.label.toLowerCase()) : `+${f.percent}% ${escapeHtml(f.label.toLowerCase())}`)).join(', ');
      return (
        `<span class="${m.odds >= 0.5 ? 'odds-good' : 'odds-bad'}"><b>${pct}%</b> to win</span> ` +
        `<span class="dim">· attack ${m.attack} vs defence ${m.baseDefence}${bonus ? ` (${bonus})` : ''} = ${m.defence.toFixed(1)}` +
        ` · ${m.attacker.hp} vs ${m.defender.hp} hp</span>`
      );
    }
  }
}

function refreshAfterCommand(): void {
  idleCityIds = new Set(engine.idleCities().map((c) => c.id));
  refreshReachable();
  refreshUnitReadout();
  refreshCityReadout();
  refreshTurnDisplay();
  needsRedraw = true;
}

/** Spend the selected settler on a city where it stands. */
function foundCity(): void {
  const unit = selectedUnit();
  if (!unit) return;
  const result = engine.apply({ t: 'FoundCity', unitId: unit.id });
  if (!result.ok) {
    log(ERROR_TEXT[result.error], 'err');
    return;
  }
  let founded: number | null = null;
  for (const event of result.value) {
    if (event.t === 'CityFounded') {
      log(`${event.name} founded.`, 'ok');
      selectCity(event.cityId);
      founded = event.cityId;
    }
  }
  // The settler is gone, so the selection has to move on.
  const waiting = engine.awaitingOrders();
  select(waiting.length > 0 ? waiting[0]!.id : null);
  refreshAfterCommand();
  refreshReadout('—');
  // A new city has nothing to build yet: open it, so the first choice is made now
  // rather than discovered idle at the end of the turn.
  if (founded !== null) cityScreen.open(founded);
}

function unitCommand(cmd: 'Fortify' | 'Sleep' | 'SkipUnit' | 'CancelOrders'): void {
  const unit = selectedUnit();
  if (!unit) return;
  if (report_(engine.apply({ t: cmd, unitId: unit.id }))) refreshAfterCommand();
}

function endTurn(): void {
  const result = engine.endTurn();
  if (!result.ok) {
    log(ERROR_TEXT[result.error], 'err');
    return;
  }

  // The per-event narration lives in the turn report now. The log keeps only the
  // things the report cannot carry — the empire-wide ones with no city to point at.
  for (const event of result.value) {
    if (event.t === 'Bankrupt' && event.civId === engine.activeCiv) {
      log('The treasury is empty and there is nothing left to sell.', 'err');
    }
    if (event.t === 'ImprovementBuilt' && event.civId === engine.activeCiv) log(`A ${event.name} was completed.`, 'ok');
  }

  refreshAfterCommand();
  refreshReadout('—');
  showReport();
  if (cityScreen.isOpen) cityScreen.refresh();
  if (citiesScreen.isOpen) citiesScreen.refresh();
  if (rivalsScreen.isOpen) rivalsScreen.refresh();
  if (governmentScreen.isOpen) governmentScreen.refresh();
  techScreen.refresh();
  void autosave();

  const outcome = engine.outcome();
  if (outcome) showOutcome(outcome);

  // A discovery that leaves nothing lined up opens the tree, the way a new era of
  // learning should: at the moment the choice matters, not whenever the player next
  // happens to look.
  const learned = result.value.some((e) => e.t === 'TechDiscovered' && e.civId === engine.activeCiv);
  if (learned && engine.researchIdle() && !cityScreen.isOpen) {
    citiesScreen.close();
    techScreen.open();
  }

  // Put the player straight on the first unit that needs them.
  const waiting = engine.awaitingOrders();
  if (waiting.length > 0) {
    select(waiting[0]!.id);
    centreOn(camera, engine.state.map, waiting[0]!.col, waiting[0]!.row);
  }
}

async function autosave(): Promise<void> {
  if (!engine.content.rules.autosave.enabled) return;
  try {
    const index = await writeAutosave(engine.state.meta.turn, engine.content.rules.autosave.slots, engine.save());
    await refreshAutosaveList();
    void index;
  } catch (error) {
    log(error instanceof Error ? error.message : String(error), 'err');
  }
}

async function refreshAutosaveList(): Promise<void> {
  const saves = await listAutosaves(engine.content.rules.autosave.slots);
  autoSelect.innerHTML = saves.length
    ? saves.map((s) => `<option value="${s.index}">Turn ${s.turn}</option>`).join('')
    : '<option value="">No autosaves yet</option>';
}

// ─── input ─────────────────────────────────────────────────────────────────────

function resizeCanvas(): void {
  const ratio = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  // Draw in CSS pixels; the backing store handles the display's density, so the map
  // is crisp on a Retina screen without every coordinate needing to know about it.
  ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);
  camera.viewportWidth = width;
  camera.viewportHeight = height;
  clampCamera(camera, engine.state.map);
  needsRedraw = true;
}

window.addEventListener('resize', resizeCanvas);

let dragging = false;
let dragDistance = 0;
let lastPointer = { x: 0, y: 0 };

/** Below this many pixels of travel, a pointer gesture counts as a click, not a drag. */
const CLICK_SLOP = 5;

// The browser's own menu would sit on top of the map every time a unit was ordered.
canvas.addEventListener('contextmenu', (event) => event.preventDefault());

canvas.addEventListener('pointerdown', (event) => {
  if (event.button === 2) {
    // Right-click orders immediately rather than on release: there is no drag gesture
    // on this button, so waiting would only add lag.
    const rect = canvas.getBoundingClientRect();
    const tile = pickTile(event.clientX - rect.left, event.clientY - rect.top);
    const flying = selectedUnit();
    if (tile && escorting) escortAt(tileIndex(engine.state.map, tile.col, tile.row));
    else if (tile && bombarding) bombardAt(tileIndex(engine.state.map, tile.col, tile.row));
    else if (tile && flying && flying.owner === engine.activeCiv && unitDef(engine.content, flying).domain === 'air') flyTo(tileIndex(engine.state.map, tile.col, tile.row));
    else if (tile) sendTo(tile.col, tile.row, event.shiftKey);
    return;
  }
  dragging = true;
  dragDistance = 0;
  lastPointer = { x: event.clientX, y: event.clientY };
  canvas.setPointerCapture(event.pointerId);
});

canvas.addEventListener('pointermove', (event) => {
  const rect = canvas.getBoundingClientRect();
  pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };

  if (dragging) {
    const dx = event.clientX - lastPointer.x;
    const dy = event.clientY - lastPointer.y;
    dragDistance += Math.abs(dx) + Math.abs(dy);
    lastPointer = { x: event.clientX, y: event.clientY };
    if (dragDistance > CLICK_SLOP) {
      canvas.classList.add('dragging');
      // In the angled view a screen pixel up the map is two pixels of ground.
      panBy(camera, engine.state.map, -dx, layers.showDimetric ? -dy / SQUASH : -dy);
    }
  }
  refreshHover();
  needsRedraw = true;
});

const endDrag = (event: PointerEvent): void => {
  if (event.button === 2) return;
  const wasClick = dragging && dragDistance <= CLICK_SLOP;
  dragging = false;
  canvas.classList.remove('dragging');
  if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);

  if (wasClick) {
    const rect = canvas.getBoundingClientRect();
    const tile = pickTile(event.clientX - rect.left, event.clientY - rect.top);
    if (tile) clickTile(tile.col, tile.row);
  }
};
canvas.addEventListener('pointerup', endDrag);
canvas.addEventListener('pointercancel', () => {
  dragging = false;
  canvas.classList.remove('dragging');
});

canvas.addEventListener('pointerleave', () => {
  pointer = null;
  hover = null;
  tooltip.hidden = true;
  needsRedraw = true;
});

canvas.addEventListener(
  'wheel',
  (event) => {
    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    // Multiplicative zoom: each notch changes the scale by a fixed ratio, so zooming
    // feels the same whether you are close in or far out.
    const factor = Math.exp(-event.deltaY * 0.0015);
    zoomTo(camera, engine.state.map, camera.tileSize * factor, event.clientX - rect.left, event.clientY - rect.top);
    refreshHover();
    needsRedraw = true;
  },
  { passive: false },
);

window.addEventListener('keydown', (event) => {
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) return;
  // The new-game screen stands in front of the game: only Escape reaches past it.
  if (newGameScreen.isOpen && event.key !== 'Escape') return;
  keysDown.add(event.key);
  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', ' ', 'F1', 'F2', 'F3', 'F4'].includes(event.key)) event.preventDefault();

  // Stepping the selected unit. Checked before the switch so the letter keys below
  // cannot shadow a direction.
  const step = STEP_KEYS[event.key.toLowerCase()];
  if (step !== undefined && !event.ctrlKey && !event.metaKey) {
    stepSelected(step);
    return;
  }

  switch (event.key) {
    case 'Enter':
      endTurn();
      break;
    case 'Tab':
      cycleUnit();
      break;
    case ' ':
      unitCommand('SkipUnit');
      break;
    case 'b':
      foundCity();
      break;
    case 'u':
      upgradeSelected();
      break;
    case 'k':
      toggleBombard();
      break;
    case 'e':
      toggleEscort();
      break;
    case 'l':
      boardOrUnload();
      break;
    case 'f':
      unitCommand('Fortify');
      break;
    case 's':
      unitCommand('Sleep');
      break;

    case 'Escape':
      if (bombarding) toggleBombard();
      else if (escorting) toggleEscort();
      else if (newGameScreen.isOpen) newGameScreen.close();
      else if (techScreen.isOpen) techScreen.close();
      else if (dealScreen.isOpen) dealScreen.close();
      else if (rivalsScreen.isOpen) rivalsScreen.close();
      else if (governmentScreen.isOpen) governmentScreen.close();
      else if (scoreScreen.isOpen) scoreScreen.close();
      else if (cityScreen.isOpen) cityScreen.close();
      else if (citiesScreen.isOpen) citiesScreen.close();
      else if (!$('panel').hidden) toggleSettings(false);
      else if (!$('report').hidden) $('report').hidden = true;
      else select(null);
      break;
    case 'F1':
      citiesScreen.isOpen ? citiesScreen.close() : citiesScreen.open();
      break;
    case 'F2':
      rivalsScreen.isOpen ? rivalsScreen.close() : rivalsScreen.open();
      break;
    case 'F3':
      governmentScreen.isOpen ? governmentScreen.close() : governmentScreen.open();
      break;
    case 'F4':
      scoreScreen.isOpen ? scoreScreen.close() : scoreScreen.open(engine.outcome() !== null);
      break;
    case 't':
      techScreen.isOpen ? techScreen.close() : techScreen.open();
      break;
    case 'c': {
      const unit = selectedUnit();
      if (unit) {
        centreOn(camera, engine.state.map, unit.col, unit.row);
        needsRedraw = true;
      }
      break;
    }
    case 'o': {
      const city = selectedCity();
      if (city) cityScreen.open(city.id);
      else log('Select one of your cities first.', 'err');
      break;
    }
    case 'g':
      toggleLayer('grid');
      break;
    case 'r':
      toggleLayer('rivers');
      break;
    case 'v':
      toggleLayer('fog');
      break;
    case 'n':
      toggleLayer('borders');
      break;
    case 'p':
      toggleLayer('dimetric');
      break;
  }
});
// A clicked button keeps the keyboard focus, and Enter on a focused button presses it
// again — so after clicking Load, Enter reloaded the save instead of ending the turn.
// Buttons give the focus back once clicked; the game's keys belong to the game.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (target instanceof HTMLButtonElement) target.blur();
});
window.addEventListener('keyup', (event) => keysDown.delete(event.key));
window.addEventListener('blur', () => keysDown.clear());

/**
 * Stepping a unit with the keyboard.
 *
 * Six directions, so the numeric keypad fits exactly — 7/9 north-west and north-east,
 * 4/6 west and east, 1/3 south-west and south-east, with 8 and 2 unused because a hex
 * grid has no straight north or south. For keyboards without a keypad the same shape
 * sits on QW / AD / ZX.
 *
 * Indices match HEX_DIRECTIONS: [E, NE, NW, W, SW, SE].
 */
const STEP_KEYS: Record<string, number> = {
  d: 0, '6': 0,
  w: 1, '9': 1,
  q: 2, '7': 2,
  a: 3, '4': 3,
  z: 4, '1': 4,
  x: 5, '3': 5,
};

const stepBuffer = new Int32Array(6);

function stepSelected(direction: number): void {
  const unit = selectedUnit();
  if (!unit || unit.owner !== engine.activeCiv) {
    log('Select one of your units first.', 'err');
    return;
  }
  if (unit.movesLeft <= 0) {
    log(`That ${engine.content.units.get(unit.type).name} has no movement left this turn.`, 'err');
    return;
  }

  const from = unitIndex(engine.state.map, unit);
  neighborsByDirection(engine.state.map, from, stepBuffer);
  const to = stepBuffer[direction]!;
  if (to === -1) {
    log('That is off the edge of the world.', 'err');
    return;
  }

  void moveOrAttack(unit, to);
}

/**
 * Step one unit onto a neighbouring tile — which may be a move, an attack, a capture,
 * or, against someone at peace, a question about war first.
 *
 * The odds are not asked about here: they are on the tooltip before the click, which
 * is when they are useful. Only war, which cannot be undone, gets a dialog.
 */
async function moveOrAttack(unit: Unit, to: number): Promise<void> {
  const preview = engine.preview(unit.id, to);
  if (preview?.kind === 'war') {
    if (preview.truce > 0) {
      log(`A truce holds for ${preview.truce} more turn${preview.truce === 1 ? '' : 's'}; you cannot attack yet.`, 'err');
      return;
    }
    if (!(await askWar(preview.civs))) return;
    for (const civ of preview.civs) {
      const declared = engine.apply({ t: 'DeclareWar', on: civ });
      if (!declared.ok) {
        log(ERROR_TEXT[declared.error], 'err');
        return;
      }
      log(`You are at war with ${engine.state.civs[civ]?.name ?? 'them'}.`, 'err');
    }
  }

  const result = engine.apply({ t: 'MoveUnit', unitId: unit.id, to });
  if (!result.ok) log(ERROR_TEXT[result.error], 'err');
  else {
    narrateFighting(result.value);
    // Follow the unit rather than making the player chase it off the screen.
    const moved = engine.state.units[unit.id];
    if (moved) centreOnIfOffscreen(moved.col, moved.row);
    else select(null);
  }
  refreshAfterCommand();
  if (rivalsScreen.isOpen) rivalsScreen.refresh();
  const outcome = engine.outcome();
  if (outcome) showOutcome(outcome);
}

/** Say what a fight or a capture did, in the log, as it happens. */
function narrateFighting(events: readonly GameEvent[]): void {
  const name = (type: number): string => engine.content.units.get(type).name;
  const civ = (id: number): string => engine.state.civs[id]?.name ?? 'someone';
  for (const event of events) {
    if (event.t === 'CombatResolved') {
      const odds = `${Math.round(event.odds * 100)}% odds`;
      log(
        event.attackerWon
          ? `Your ${name(event.attackerType)} destroyed ${civ(event.defenderOwner)}'s ${name(event.defenderType)} (${odds}; ${event.attackerHp} hp left).`
          : `Your ${name(event.attackerType)} was destroyed by ${civ(event.defenderOwner)}'s ${name(event.defenderType)} (${odds}).`,
        event.attackerWon ? 'ok' : 'err',
      );
    } else if (event.t === 'UnitPromoted') {
      log(`Your ${name(engine.state.units[event.unitId]?.type ?? 0)} is now ${event.rank}.`, 'ok');
    } else if (event.t === 'CargoLost') {
      log(`${event.count} unit${event.count === 1 ? '' : 's'} went down with the ship.`, event.owner === engine.activeCiv ? 'err' : 'ok');
    } else if (event.t === 'UnitBoarded') {
      log('Aboard — it sails with the ship.', 'ok');
    } else if (event.t === 'UnitCaptured') {
      log(`Captured ${civ(event.from)}'s ${name(event.unitType)}.`, 'ok');
    } else if (event.t === 'CityCaptured') {
      log(`You have taken ${event.name}${event.lost.length ? ` — lost in the fighting: ${event.lost.join(', ')}` : ''}.`, 'ok');
    } else if (event.t === 'CivDefeated') {
      log(`${event.name} has been destroyed.`, 'ok');
    }
  }
}

/** The end of the game, either way. */
/**
 * The end of the game, shown once, the moment it happens (ADR-0061). A game loaded
 * after it ended does not ambush the player: the end screen is under Score (F4).
 */
let outcomeShown: string | null = null;
function showOutcome(outcome: 'victory' | 'defeat'): void {
  const key = `${engine.state.meta.seed}:${outcome}`;
  if (outcomeShown === key) return;
  outcomeShown = key;
  scoreScreen.open(true);
}

/** Recentre only when the tile has gone out of view, so the map does not lurch. */
function centreOnIfOffscreen(col: number, row: number): void {
  const range = visibleRangeMargin();
  if (row < range.minRow || row > range.maxRow || col < range.minCol || col > range.maxCol) {
    centreOn(camera, engine.state.map, col, row);
  }
  needsRedraw = true;
}

/** The tile under a screen point, in whichever view is showing. */
function pickTile(x: number, y: number): { col: number; row: number } | null {
  return layers.showDimetric ? dimetricTileAt(camera, engine.state.map, x, y) : tileAtScreen(camera, engine.state.map, x, y);
}

function visibleRangeMargin(): { minCol: number; maxCol: number; minRow: number; maxRow: number } {
  const half = { x: camera.viewportWidth / 2, y: camera.viewportHeight / 2 };
  const step = { x: Math.sqrt(3) * camera.tileSize, y: 1.5 * camera.tileSize };
  const centre = pickTile(half.x, half.y);
  const cols = Math.floor(half.x / step.x) - 1;
  const rows = Math.floor(half.y / step.y) - 1;
  return {
    minCol: (centre?.col ?? 0) - cols,
    maxCol: (centre?.col ?? 0) + cols,
    minRow: (centre?.row ?? 0) - rows,
    maxRow: (centre?.row ?? 0) + rows,
  };
}

function toggleLayer(which: 'grid' | 'rivers' | 'fog' | 'borders' | 'dimetric'): void {
  const box = $<HTMLInputElement>(`layer-${which}`);
  box.checked = !box.checked;
  box.dispatchEvent(new Event('change'));
}

// ─── hover and the tooltip ─────────────────────────────────────────────────────

function refreshHover(): void {
  if (!pointer) return;
  hover = pickTile(pointer.x, pointer.y);
  updateTooltip();
}

function updateTooltip(): void {
  if (!hover || !pointer || (dragging && dragDistance > CLICK_SLOP)) {
    tooltip.hidden = true;
    return;
  }

  const map = engine.state.map;
  const content = engine.content;
  const index = hover.row * map.width + hover.col;
  const vision = engine.state.vision[engine.activeCiv];
  const known = !layers.showFog || !vision || vision[index] !== VIS_UNKNOWN;

  if (!known) {
    tooltip.innerHTML = '<strong>Unexplored</strong>';
    tooltip.hidden = false;
    positionTooltip();
    return;
  }

  const terrain = content.terrain.get(map.terrain[index]!);
  const feature = content.features.get(map.feature[index]!);
  // A strategic resource the viewer has not revealed is not there, as far as it knows.
  const viewer = engine.state.civs[engine.activeCiv];
  const rawResource = map.resource[index]!;
  const resource = content.resources.get(resourceKnown(content, viewer, rawResource) ? rawResource : 0);

  // The engine's own sum — terrain, feature, resource, improvement, road and river — so
  // the tooltip and the city screen can never disagree. (This used to keep its own copy
  // of the rule, and stopped counting farms and roads when they arrived.)
  const { food, production, gold } = tileYield(map, content, index, viewer);

  const lines: string[] = [
    `<strong>${escapeHtml(terrain.name)}</strong>${feature.key !== 'none' ? ` · ${escapeHtml(feature.name)}` : ''}`,
  ];
  if (resource.key !== 'none') {
    lines.push(`<span class="res">${escapeHtml(resource.name)}</span> <span class="dim">${resource.class}</span>`);
    if (resource.class === 'strategic') {
      const users = content.units.all.filter((u) => u.civId === -1 && u.resourceIds.includes(resource.id)).map((u) => u.name);
      if (users.length) lines.push(`<span class="dim">needed for ${escapeHtml(users.join(', '))}</span>`);
    }
  }
  if (map.rivers[index] !== 0) lines.push('<span class="dim">River</span>');
  const improvement = content.improvements.get(map.improvement[index]!);
  const road = map.road[index] === 1 && content.route ? content.route.name : null;
  const built = [improvement.key !== 'none' ? improvement.name : null, road].filter(Boolean).join(' · ');
  if (built) lines.push(`<span class="res">${escapeHtml(built)}</span>`);

  // Units, but only the ones the viewer can actually see right now.
  if (!layers.showFog || !vision || vision[index] === VIS_VISIBLE) {
    for (const unit of Object.values(engine.state.units)) {
      if (unitIndex(map, unit) !== index) continue;
      const def = unitDef(content, unit);
      const civ = engine.state.civs[unit.owner];
      const hostile = unit.owner !== engine.activeCiv && atWar(engine.state, engine.activeCiv, unit.owner);
      const rank = rankOf(content, unit);
      const aboard = unit.aboard !== NONE ? ' · aboard' : '';
      const carrying =
        def.capacity > 0
          ? ` · carrying ${cargoOf(engine.state, unit).length}/${def.capacity}`
          : def.airCapacity > 0
            ? ` · ${cargoOf(engine.state, unit).length}/${def.airCapacity} aircraft`
            : '';
      lines.push(
        `<span class="res">${escapeHtml(def.name)}</span> <span class="dim">${escapeHtml(civ?.name ?? '')}` +
          `${rank.index > 0 ? ` · ${escapeHtml(rank.name)}` : ''} · ${def.attack}/${def.defence} · ${unit.hp}/${maxHp(content, unit)} hp${aboard}${carrying}</span>` +
          `${hostile ? ' <span class="odds-bad">enemy</span>' : ''}`,
      );
    }
    // The odds, before the click — the brief's "transparent and strategically
    // understandable" combat is this line.
    const selected = selectedUnit();
    if (bombarding && selected && reachable?.has(index)) {
      lines.push(bombardLine(selected, index));
    } else if (selected && selected.owner === engine.activeCiv && unitDef(content, selected).domain === 'air' && reachable?.has(index)) {
      lines.push(airLine(selected, index));
    } else if (selected && selected.owner === engine.activeCiv && isAdjacent(unitIndex(map, selected), index)) {
      const preview = engine.preview(selected.id, index);
      const line = preview ? attackLine(preview) : null;
      if (line) lines.push(line);
    }
  }

  lines.push(`<span class="yields"><b>${food}</b> food · <b>${production}</b> prod · <b>${gold}</b> gold</span>`);
  lines.push(
    `<span class="dim">(${hover.col}, ${hover.row}) · elevation ${map.elevation[index]} · ` +
      `move ${terrain.passable ? terrain.movementCost + (feature.movementCost || 0) : '—'}` +
      `${terrain.defenceBonus + feature.defenceBonus > 0 ? ` · +${terrain.defenceBonus + feature.defenceBonus}% defence` : ''}</span>`,
  );

  tooltip.innerHTML = lines.join('<br>');
  tooltip.hidden = false;
  positionTooltip();
}

/** Keep the tooltip inside the window rather than letting it run off the edge. */
function positionTooltip(): void {
  if (!pointer) return;
  const offset = 16;
  const rect = tooltip.getBoundingClientRect();
  const canvasRect = canvas.getBoundingClientRect();
  let left = pointer.x + offset;
  let top = pointer.y + offset;
  if (left + rect.width > canvasRect.width) left = pointer.x - rect.width - offset;
  if (top + rect.height > canvasRect.height) top = pointer.y - rect.height - offset;
  tooltip.style.left = `${Math.max(4, left)}px`;
  tooltip.style.top = `${Math.max(4, top)}px`;
}

// ─── controls ──────────────────────────────────────────────────────────────────

$('end-turn').addEventListener('click', endTurn);
$('cities').addEventListener('click', () => citiesScreen.open());
$('rivals').addEventListener('click', () => rivalsScreen.open());
$('government').addEventListener('click', () => governmentScreen.open());
$('score').addEventListener('click', () => scoreScreen.open(engine.outcome() !== null));
$('research').addEventListener('click', () => techScreen.open());
$('rate-down').addEventListener('click', () => stepScienceRate(-1));
$('rate-up').addEventListener('click', () => stepScienceRate(1));

$('open-city').addEventListener('click', () => {
  const city = selectedCity();
  if (city) cityScreen.open(city.id);
  else log('Select one of your cities first.', 'err');
});

$('cmd-found').addEventListener('click', foundCity);
$('cmd-fortify').addEventListener('click', () => unitCommand('Fortify'));
$('cmd-sleep').addEventListener('click', () => unitCommand('Sleep'));
$('cmd-skip').addEventListener('click', () => unitCommand('SkipUnit'));
$('cmd-cancel').addEventListener('click', () => unitCommand('CancelOrders'));

$('regen').addEventListener('click', () => generate(sizeSelect.value));
sizeSelect.addEventListener('change', () => generate(sizeSelect.value));
seedInput.addEventListener('keydown', (event) => {
  if (event.key === 'Enter') generate(sizeSelect.value);
});

$('random-seed').addEventListener('click', () => {
  const words = ['meridian', 'lantern', 'basalt', 'harrow', 'kestrel', 'tessera', 'oxbow', 'cinder', 'vellum', 'quarry'];
  const pick = (): string => words[Math.floor(Math.random() * words.length)]!;
  seedInput.value = `${pick()}-${pick()}-${String(Math.floor(Math.random() * 100)).padStart(2, '0')}`;
  generate(sizeSelect.value);
});

// The settings panel sits over the map; the map keeps its size underneath.
const toggleSettings = (open = $('panel').hidden): void => {
  $('panel').hidden = !open;
  $('rail-saves').classList.toggle('on', open);
};
$('toggle-panel').addEventListener('click', () => toggleSettings());
$('rail-saves').addEventListener('click', () => toggleSettings());
$('close-panel').addEventListener('click', () => toggleSettings(false));
$('rail-research').addEventListener('click', () => techScreen.open());
$('rail-map').addEventListener('click', () => toggleSettings(false));

$<HTMLInputElement>('layer-dimetric').checked = layers.showDimetric;
for (const which of ['grid', 'rivers', 'resources', 'fog', 'borders', 'dimetric'] as const) {
  $<HTMLInputElement>(`layer-${which}`).addEventListener('change', (event) => {
    const checked = (event.target as HTMLInputElement).checked;
    if (which === 'grid') layers.showGrid = checked;
    if (which === 'rivers') layers.showRivers = checked;
    if (which === 'resources') layers.showResources = checked;
    if (which === 'borders') layers.showBorders = checked;
    if (which === 'dimetric') {
      layers.showDimetric = checked;
      rememberView(checked);
      // Keep looking at the same place: the angled view squashes the screen's vertical.
      refreshHover();
    }
    if (which === 'fog') {
      layers.showFog = checked;
      refreshReachable();
    }
    needsRedraw = true;
  });
}

$('reset-params').addEventListener('click', () => {
  const defaults = engine.content.mapgen.defaults as unknown as Record<string, number>;
  for (const key of Object.keys(overrides)) delete (overrides as Record<string, unknown>)[key];
  for (const t of TUNABLE) {
    $<HTMLInputElement>(`p-${t.key}`).value = String(defaults[t.key]);
    $(`o-${t.key}`).textContent = String(defaults[t.key]);
  }
  generate(sizeSelect.value);
  log('Parameters reset to the values in mapgen-presets.json.');
});

$('save').addEventListener('click', () => {
  const slot = Number(slotSelect.value);
  void writeSlot(slot, engine.save())
    .then(() => log(`Saved to slot ${slot} (hash ${engine.hash()}).`, 'ok'))
    .catch((error: unknown) => log(error instanceof Error ? error.message : String(error), 'err'));
});

function adoptLoaded(json: string, description: string): void {
  engine = loadEngine(json);
  // A game that had already ended when it was saved does not announce it again.
  const ended = engine.outcome();
  outcomeShown = ended ? `${engine.state.meta.seed}:${ended}` : null;
  seedInput.value = engine.state.meta.seed;
  report = reportFromState();
  camera = createCamera(engine.state.map, canvas.clientWidth, canvas.clientHeight);

  const first = unitsOf(engine.state, engine.activeCiv)[0];
  if (first) {
    centreOn(camera, engine.state.map, first.col, first.row);
    select(first.id);
  } else {
    select(null);
  }

  cityScreen.close();
  citiesScreen.close();
  techScreen.close();
  $('report').hidden = true;
  idleCityIds = new Set(engine.idleCities().map((c) => c.id));
  selectCity(null);
  refreshReadout('loaded from a save');
  refreshTurnDisplay();
  needsRedraw = true;
  log(`${description} → turn ${engine.state.meta.turn}, hash ${engine.hash()}`, 'ok');
}

$('load').addEventListener('click', () => {
  const slot = Number(slotSelect.value);
  void readSlot(slot)
    .then((json) => {
      if (!json) {
        log(`Slot ${slot} is empty.`, 'err');
        return;
      }
      adoptLoaded(json, `Loaded slot ${slot}`);
    })
    .catch((error: unknown) => log(error instanceof Error ? error.message : String(error), 'err'));
});

$('load-auto').addEventListener('click', () => {
  const index = Number(autoSelect.value);
  if (!index) return;
  void readAutosave(index)
    .then((json) => {
      if (!json) {
        log('That autosave is gone.', 'err');
        return;
      }
      adoptLoaded(json, 'Loaded autosave');
    })
    .catch((error: unknown) => log(error instanceof Error ? error.message : String(error), 'err'));
});

$('delete').addEventListener('click', () => {
  const slot = Number(slotSelect.value);
  void clearSlot(slot).then(() => log(`Cleared slot ${slot}.`));
});

void listSlots().then((slots) => {
  if (slots.length > 0) log(`Occupied slots: ${slots.map((s) => `${s.slot} (${s.seed})`).join(', ')}`);
});

/** Rebuild the report from the map, for a world loaded from a save. */
function reportFromState(): WorldReport {
  const map = engine.state.map;
  const content = engine.content;
  const lakeId = content.terrain.id('lake');
  const landMask = new Uint8Array(map.terrain.length);
  const lakeMask = new Uint8Array(map.terrain.length);
  const riverMask = new Uint8Array(map.terrain.length);
  const placed: Record<string, number> = {};
  let landTiles = 0;
  let riverTiles = 0;

  for (let i = 0; i < map.terrain.length; i++) {
    if (!content.terrain.get(map.terrain[i]!).water) {
      landMask[i] = 1;
      landTiles++;
    }
    if (map.terrain[i] === lakeId) lakeMask[i] = 1;
    if (map.rivers[i] !== 0) {
      riverMask[i] = 1;
      if (landMask[i] === 1) riverTiles++;
    }
    if (map.resource[i] !== 0) {
      const key = content.resources.get(map.resource[i]!).key;
      placed[key] = (placed[key] ?? 0) + 1;
    }
  }

  return {
    landTiles,
    waterTiles: map.terrain.length - landTiles,
    lakeCount: findRegions(map, lakeMask, 1).sizes.length,
    drownedIslandTiles: 0,
    riverCount: findRegions(map, riverMask, 1).sizes.length,
    riverTiles,
    resources: { placed, shortfalls: [], total: Object.values(placed).reduce((a, b) => a + b, 0) },
    continentSizes: findRegions(map, landMask, 1).sizes.sort((a, b) => b - a),
    openWater: openWaterStats(map, landMask),
  };
}

boot();
void refreshAutosaveList();
